/**
 * Login submission hooks
 * Extracted from useLoginController.ts to reduce file size
 */

import { useCallback } from "react";
import { useRouter } from "next/router";
import { ValidationError } from "class-validator";
import Auth from "@Front/Api/Auth/Customer/Auth";
import { CustomerStore } from "@Front/Stores/CustomerStore";
import { getLeCoffreAccessTokenCookie } from "@Front/Utils/lecoffreSessionCookieHelpers";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { validatePassword } from "@Front/Utils/formValidators";
import { LoginStep } from "../loginSteps";
import { handleEmailSubmitError, redirectAfterLogin, handleTotpVerificationError } from "./loginErrorHandlers";
import { handleTotpVerificationResult } from "./loginTotpHandlers";
import {
	buildCustomerLoginAuthScopePayload,
	type CustomerAuthDisambiguation,
} from "@Front/Components/Layouts/Login/helpers/loginFolderUidHelpers";
import {
	runCustomerMailVerifySmsStep,
	runLoginEmailFormSubmit,
	type UseEmailSubmissionOptions,
} from "./loginEmailSubmissionExecution";

export type { UseEmailSubmissionOptions };
export { runCustomerMailVerifySmsStep };

/**
 * Hook for email submission
 */
export function useEmailSubmission(
	opts: UseEmailSubmissionOptions,
): (e: React.FormEvent<HTMLFormElement> | null, values: { [key: string]: string }) => Promise<void> {
	const { router, setValidationErrors } = opts;
	return useCallback(
		async (_e: React.FormEvent<HTMLFormElement> | null, values: { [key: string]: string }) => {
			try {
				await runLoginEmailFormSubmit(values, opts);
			} catch (error: unknown) {
				handleEmailSubmitError(error, values, router, setValidationErrors);
			}
		},
		[opts, router, setValidationErrors],
	);
}

export interface UseSmsCodeSubmissionOptions {
	email: string;
	router: ReturnType<typeof useRouter>;
	loginFolderUid: string | undefined;
	loginOfficeUid: string | undefined;
	loginCustomerUid: string | undefined;
	authDisambiguation: CustomerAuthDisambiguation;
	setStep: (step: LoginStep) => void;
	setTotpCode: (code: string) => void;
	setValidationErrors: (errors: ValidationError[]) => void;
	refresh: () => Promise<void>;
}

/**
 * Hook for SMS code submission
 */
export function useSmsCodeSubmission(opts: UseSmsCodeSubmissionOptions): (e: React.FormEvent<HTMLFormElement> | null, values: { [key: string]: string }) => Promise<void> {
	const { email, router, loginFolderUid, loginOfficeUid, loginCustomerUid, authDisambiguation, setStep, setTotpCode, setValidationErrors, refresh } =
		opts;
	return useCallback(
		async (_e: React.FormEvent<HTMLFormElement> | null, values: { [key: string]: string }) => {
			try {
				if (!values["totpCode"]) return;
				const res = await Auth.getInstance().verifyTotpCode({
					totpCode: values["totpCode"],
					email,
					...buildCustomerLoginAuthScopePayload(loginFolderUid, loginOfficeUid, authDisambiguation, loginCustomerUid),
				});
				handleTotpVerificationResult({
					res,
					values,
					email,
					router,
					loginFolderUid,
					loginOfficeUid,
					loginCustomerUid,
					authDisambiguation,
					setStep,
					setTotpCode,
					setValidationErrors,
					refresh,
				});
			} catch (error: unknown) {
				handleTotpVerificationError(error, email, values["totpCode"], setValidationErrors);
			}
		},
		[email, router, loginFolderUid, loginOfficeUid, loginCustomerUid, authDisambiguation, setStep, setTotpCode, setValidationErrors, refresh],
	);
}

/**
 * Hook for password validation
 */
export function usePasswordValidation(): (values: { [key: string]: string }) => ValidationError[] | null {
	return useCallback((values: { [key: string]: string }): ValidationError[] | null => {
		const password = values["password"];
		if (!password) {
			return null;
		}

		if (password !== values["confirm_password"]) {
			return [
				{
					property: "confirm_password",
					constraints: {
						"400": "Les mots de passe ne correspondent pas.",
					},
				},
			];
		}

		const passwordValidation = validatePassword(password);
		if (!passwordValidation.isValid && passwordValidation.error) {
			return [passwordValidation.error];
		}
		return null;
	}, []);
}

/**
 * Hook for connect and redirect
 */
export function useConnectAndRedirect(
	router: ReturnType<typeof useRouter>,
	refresh: () => Promise<void>,
	setValidationErrors: (errors: ValidationError[]) => void,
): (token: { accessToken: string; refreshToken: string }) => Promise<void> {
	return useCallback(
		async (token: { accessToken: string; refreshToken: string }) => {
			CustomerStore.instance.connect(token.accessToken, token.refreshToken);

			await new Promise((resolve) => setTimeout(resolve, 100));

			const cookieCheck = getLeCoffreAccessTokenCookie();
			if (!cookieCheck) {
				LoggerService.warn("⚠️ [Login] Cookie not available after connect, retrying...");
				await new Promise((resolve) => setTimeout(resolve, 200));
				const retryCookieCheck = getLeCoffreAccessTokenCookie();
				if (!retryCookieCheck) {
					LoggerService.error("❌ [Login] Cookie still not available after retry");
					throw new Error("Erreur lors de la connexion");
				}
			}

			await refresh();
			setValidationErrors([]);
			redirectAfterLogin(router);
		},
		[router, refresh, setValidationErrors],
	);
}

export { usePasswordSubmissions, usePasswordForgot } from "./loginPasswordAndResendHooks";
export type { UsePasswordSubmissionsOptions, UsePasswordForgotOptions } from "./loginPasswordAndResendHooks";
export { useSendAnotherCode } from "./loginSendAnotherCodeHook";
export type { UseSendAnotherCodeOptions } from "./loginSendAnotherCodeHook";
